import React from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import Button from "../component-lib/Molecules/button/Button";
import {
  CUSTOM_lABEL_STYLE,
  ORDER_QUANTITY_UNIT,
  RX_ORDER_DETAILS,
  FREQUENCY_OPTIONS,
} from "../screens/patient-screen/visit-flow/constant";
import SelectDropdown from "../component-lib/Atoms/Select/Select";
import Heading from "../component-lib/Atoms/heading/EventEHRHeading";
import Input from "../component-lib/Atoms/Input/Input";
import useDeviceType from "../custom-hooks/DeviceType";
import { TEXT_CONSTANTS } from "../utils/text-constants/textConstants";
import { useDispatch, useSelector } from "react-redux";
import EventEHRLazyDropdown from "../component-lib/Molecules/lazy-loading-search/EventEHRLazyLoadingSearch";
import {
  hideLoader,
  showLoader,
} from "../component-lib/Molecules/loader/LoaderSlice";
import { getLazyLoadingApiResponse } from "../utils/constantFunctions";
import { searchIcon } from "../assets";
import { componentKey } from "../screens/patient-screen/patientSlice";
import { getLoginUserData } from "../utils/getToken";

interface RxOrderFormProps {
  getFormData: (data: any) => void;
}

export default function RxOrderForm({ getFormData }: RxOrderFormProps) {
  const { visitNoteCareFlowData } = useSelector(
    (state: any) => state[componentKey]
  );
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const [pageCount, setPageCount] = React.useState(1);
  const [dropdownSearch, setDropdownSearch] = React.useState("");
  const [dropdownOptions, setDropdownOptions] = React.useState<any>([]);
  const [selectedValue, setSelectedValue] = React.useState(null);
  const [resetFlag, setResetFlag] = React.useState(false);
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
  const parseLoginUserdata: any = getLoginUserData();
  const loginUserdata = JSON.parse(parseLoginUserdata);

  const validationSchema = Yup.object().shape({
    [RX_ORDER_DETAILS?.MEDICATION_NAME]: Yup.string().required(
      "Medication Name is required"
    ),
    [RX_ORDER_DETAILS?.ORDER_QUANTITY]: Yup.string().required(
      "Quantity is required"
    ),
    [RX_ORDER_DETAILS?.UNIT]: Yup.string().required("Unit is required"),
    [RX_ORDER_DETAILS?.NOTES]: Yup.string().required("Notes is required"),
    [RX_ORDER_DETAILS?.FREQUENCY]: Yup.string().required("Frequency is required"),
  });

  React.useEffect(() => {
    getDropdownList();
  }, []);

  React.useEffect(() => {
    if (resetFlag) {
      resetList();
    }
  }, [resetFlag]);

  const resetList = () => {
    setSelectedValue(null);
    setDropdownSearch("");
    setPageCount(1);
    getDropdownList();
  };

  // IF SEARCH VALUE  THEN WE ARE GIVEN SOME TIMER
  const handleSearchedValueChange = (data: any) => {
    if (timer) {
      clearTimeout(timer); // Clear the previous timer
    }
    // Set a new timer with your desired delay (e.g., 500ms)
    const newTimer = setTimeout(() => {
      setDropdownSearch(data);
    }, 500); // Adjust the delay as needed (in milliseconds)

    setTimer(newTimer); // Store the timer ID for cleanup
  };

  // Load the complete facility medication/inventory list (all pages) so the
  // autocomplete filters the full list client-side. Mirrors the ICD/DSM and
  // Bundle Order fix; the previous limit=6 lazy load + server search was slow
  // and showed incomplete data.
  const getDropdownList = async () => {
    dispatch(showLoader());
    try {
      const facilityId = loginUserdata?.customAttributes?.facilityId;
      const mapRows = (list: any) =>
        (Array.isArray(list) ? list : []).map((data: any) => ({
          label: `${data?.inventory?.itemName}`,
          value: `${data?.id}`,
          unitOfMeasurement: `${data?.inventory?.unitOfMeasure}`,
        }));
      const fetchPage = (page: number) =>
        getLazyLoadingApiResponse(
          "facility/facility/inventory",
          page,
          100,
          facilityId,
          true
        );

      const first = await fetchPage(1);
      let options = mapRows(first?.data?.data);
      const totalPages = first?.data?.totalPages ?? 1;

      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            fetchPage(i + 2).then((r) => mapRows(r?.data?.data))
          )
        );
        options = options.concat(...rest);
      }

      setDropdownOptions(options);
      setResetFlag(false);
    } catch (error) {
      console.log("error:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <Formik
      initialValues={{
        [RX_ORDER_DETAILS?.MEDICATION_NAME]: "",
        [RX_ORDER_DETAILS?.ORDER_QUANTITY]: "",
        [RX_ORDER_DETAILS?.UNIT]: "",
        [RX_ORDER_DETAILS?.NOTES]: "",
        [RX_ORDER_DETAILS?.FREQUENCY]: "",
        [RX_ORDER_DETAILS?.MEDICATION_NAME_TITLE]: "",
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        try {
          getFormData(values);
        } catch (error) {
          console.log(error);
        } finally {
          setSubmitting(false); // Reset submitting state
        }
      }}
    >
      {({
        errors,
        values,
        touched,
        isValid,
        dirty,
        setFieldValue,
        handleBlur,
        handleSubmit,
      }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <div
              className="h-full flex flex-col justify-between gap-6"
              style={{
                height: deviceType === "mobile" ? "40vh" : "fit-content",
                overflow: "auto",
              }}
            >
              {/* MEDICATION SEARCH DROPDOWN */}
              <div>
                <Heading
                  title={TEXT_CONSTANTS?.MEDICATION_NAME}
                  customStyle={CUSTOM_lABEL_STYLE}
                />
                <EventEHRLazyDropdown
                  // label="Search"
                  placeholder="Search"
                  startIcon={
                    <img
                      alt="search-icon"
                      src={searchIcon}
                      width={"18px"}
                      height={"18px"}
                    />
                  }
                  onBlur={handleBlur}
                  error={
                    touched[RX_ORDER_DETAILS?.MEDICATION_NAME] &&
                    Boolean(errors[RX_ORDER_DETAILS?.MEDICATION_NAME])
                  }
                  helperText={
                    touched[RX_ORDER_DETAILS?.MEDICATION_NAME]
                      ? errors[RX_ORDER_DETAILS?.MEDICATION_NAME]
                      : undefined
                  }
                  dropdownOptions={dropdownOptions}
                  dropdownSearch={dropdownSearch}
                  selectedValue={selectedValue}
                  setDropdownSearch={handleSearchedValueChange}
                  setPageCount={(data) => {
                    setPageCount(data);
                  }}
                  setResetFlag={(data) => {
                    setResetFlag(data);
                  }}
                  setSelectedValue={(data) => {
                    setFieldValue(
                      RX_ORDER_DETAILS?.MEDICATION_NAME,
                      data?.value
                    );

                    setFieldValue(
                      RX_ORDER_DETAILS?.MEDICATION_NAME_TITLE,
                      data?.label
                    );

                    setFieldValue(
                      RX_ORDER_DETAILS?.UNIT,
                      data?.unitOfMeasurement
                    );
                    setSelectedValue(data);
                  }}
                />
              </div>
              {/* QUANTITY AND UNIT DROPDOWN */}
              <div className="flex gap-3">
                {/* Qty */}
                <div className=" w-full">
                  <Heading title="Qty" customStyle={CUSTOM_lABEL_STYLE} />

                  <Input
                    // disabled
                    placeholder="Qty"
                    // isStarPresent={true}
                    name={RX_ORDER_DETAILS?.ORDER_QUANTITY}
                    value={values[RX_ORDER_DETAILS?.ORDER_QUANTITY]}
                    type="number"
                    onChangeCb={(e) => {
                      setFieldValue(
                        RX_ORDER_DETAILS?.ORDER_QUANTITY,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[RX_ORDER_DETAILS?.ORDER_QUANTITY] &&
                      Boolean(errors[RX_ORDER_DETAILS?.ORDER_QUANTITY])
                    }
                    helperText={
                      touched[RX_ORDER_DETAILS?.ORDER_QUANTITY]
                        ? errors[RX_ORDER_DETAILS?.ORDER_QUANTITY]
                        : undefined
                    }
                  />
                </div>
                {/* Unit */}
                <div className="w-full">
                  <Heading title="Unit" customStyle={CUSTOM_lABEL_STYLE} />
                  <SelectDropdown
                    // label="Incident Location"
                    // isStarPresent={true}
                    disabled={true}
                    displayEmpty={true}
                    placeholderValue="Unit"
                    name={RX_ORDER_DETAILS?.UNIT}
                    // placeholderValue="Select Rx Order"
                    value={values[RX_ORDER_DETAILS?.UNIT]}
                    options={ORDER_QUANTITY_UNIT}
                    onChangeCb={(e) =>
                      setFieldValue(RX_ORDER_DETAILS?.UNIT, e.target.value)
                    }
                    error={
                      touched[RX_ORDER_DETAILS?.UNIT] &&
                      Boolean(errors[RX_ORDER_DETAILS?.UNIT])
                    }
                    helperText={
                      touched[RX_ORDER_DETAILS?.UNIT]
                        ? errors[RX_ORDER_DETAILS?.UNIT]
                        : undefined
                    }
                  />
                </div>
              </div>
              {/* NOTES */}
              <div>
                <Heading
                  title={TEXT_CONSTANTS?.NOTES}
                  customStyle={CUSTOM_lABEL_STYLE}
                />
                <Input
                  multiline={true}
                  rows={2}
                  // textFieldLabel="Enter Chief Complaint"
                  placeholder={TEXT_CONSTANTS?.NOTES}
                  name={RX_ORDER_DETAILS.NOTES}
                  value={values[RX_ORDER_DETAILS.NOTES]}
                  onChangeCb={(e) => {
                    setFieldValue(RX_ORDER_DETAILS.NOTES, e.target.value);
                  }}
                  onBlur={handleBlur}
                  size="small"
                  error={
                    touched[RX_ORDER_DETAILS.NOTES] &&
                    Boolean(errors[RX_ORDER_DETAILS.NOTES])
                  }
                  helperText={
                    touched[RX_ORDER_DETAILS.NOTES]
                      ? errors[RX_ORDER_DETAILS.NOTES]
                      : undefined
                  }
                />
              </div>
              {/* FREQUENCY */}
              <div>
                <Heading
                  title={TEXT_CONSTANTS?.FREQUENCY}
                  customStyle={CUSTOM_lABEL_STYLE}
                />
                <SelectDropdown
                  label="Frequency"
                  // isStarPresent={true}
                  // displayEmpty={true}
                  // placeholderValue="Select Frequency"
                  name={RX_ORDER_DETAILS.FREQUENCY}
                  value={values[RX_ORDER_DETAILS.FREQUENCY]}
                  options={FREQUENCY_OPTIONS}
                  onChangeCb={(e) =>
                    setFieldValue(RX_ORDER_DETAILS.FREQUENCY, e.target.value)
                  }
                  error={
                    touched[RX_ORDER_DETAILS.FREQUENCY] &&
                    Boolean(errors[RX_ORDER_DETAILS.FREQUENCY])
                  }
                  helperText={
                    touched[RX_ORDER_DETAILS.FREQUENCY]
                      ? errors[RX_ORDER_DETAILS.FREQUENCY]
                      : undefined
                  }
                />
              </div>
            </div>

            {/* ACTION BUTTON */}
            <div className="mt-9 text-end">
              <Button
                // disabled={!isValid} // Button disabled if form is invalid or submitting
                type="submit"
                label="Add Order"
                variant="contained"
                onClickCb={() => {}}
                style={{
                  padding: "8px 55px",
                  fontSize: "16px",
                }}
              ></Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
